import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTaskManager } from '@/contexts/TaskManagerContext'
import { usePageVisibility } from '@/hooks/usePageVisibility'
import { queryKeys } from '@/lib/query-keys'
import { CREDIT_POLL_INTERVAL_MS } from '@/lib/constants'
import { getTaskExecutionKey } from '@/lib/task-lifecycle'

/**
 * Hook that auto-invalidates relevant query caches when tasks are registered or complete.
 * Should be used once in a high-level component (e.g., AppLayout).
 *
 * Invalidation strategy:
 * - Task registration: invalidate credits (credits are deducted when task is created)
 * - Task completion: invalidate credits + entity-specific data
 * - Design tasks (including listing_generation): invalidate batches too (design may be in a batch)
 */
export function useTaskCompletionInvalidation() {
  const { activeTasks, recentCompleted } = useTaskManager()
  const queryClient = useQueryClient()
  const isVisible = usePageVisibility()
  const processedCompletedRef = useRef<Set<string>>(new Set())
  const processedRegisteredRef = useRef<Set<string>>(new Set())
  const hasActiveTasks = activeTasks.size > 0

  // Track registered tasks and invalidate credits (credits are deducted on task creation)
  useEffect(() => {
    for (const task of activeTasks.values()) {
      const executionKey = getTaskExecutionKey(task)
      if (processedRegisteredRef.current.has(executionKey)) continue
      processedRegisteredRef.current.add(executionKey)

      // Credits are deducted when task is created, so refetch immediately
      void queryClient.refetchQueries({ queryKey: queryKeys.credits })
    }
  }, [activeTasks, queryClient])

  // Handle task completions
  useEffect(() => {
    for (const task of recentCompleted) {
      // Skip if we've already processed this task
      const executionKey = getTaskExecutionKey(task)
      if (processedCompletedRef.current.has(executionKey)) continue
      processedCompletedRef.current.add(executionKey)

      // Always refetch credits after task completion
      void queryClient.refetchQueries({ queryKey: queryKeys.credits })

      // Invalidate entity-specific data based on task type
      if (task.entityType === 'design') {
        // Invalidate the specific design detail
        void queryClient.invalidateQueries({
          queryKey: queryKeys.designs.detail(task.entityId),
        })
        // Invalidate design lists as status may have changed
        void queryClient.invalidateQueries({
          queryKey: queryKeys.designs.all,
        })
        // IMPORTANT: Also invalidate ALL batch queries because this design may be in a batch
        // and the batch's statistics (with_listing_count, progress) need to update
        void queryClient.invalidateQueries({ queryKey: queryKeys.batches.all })
      } else if (task.entityType === 'batch') {
        // Batch operations affect both batches and their contained designs
        void queryClient.invalidateQueries({ queryKey: queryKeys.batches.all })
        void queryClient.invalidateQueries({ queryKey: queryKeys.designs.all })
      }
    }
  }, [recentCompleted, queryClient])

  // While tasks are active, poll credits to catch any delayed backend updates
  useEffect(() => {
    if (!hasActiveTasks || !isVisible) return

    const interval = setInterval(() => {
      void queryClient.refetchQueries({ queryKey: queryKeys.credits })
    }, CREDIT_POLL_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [hasActiveTasks, isVisible, queryClient])
}
